import { Link } from "react-router-dom";

const STATUS_BADGE = {
  Draft: "bg-gray-100 text-gray-700",
  "Offered to Vendor": "bg-purple-100 text-purple-700",
  "Offer Accepted": "bg-green-100 text-green-700",
  "Offer Rejected": "bg-danger-100 text-danger-700",
  Started: "bg-brand-100 text-brand-700",
  Completed: "bg-teal-100 text-teal-700",
  Hold: "bg-warning-100 text-warning-700",
  "Completion Accepted": "bg-green-100 text-green-700",
  "Completion Rejected": "bg-danger-100 text-danger-700",
  Cancelled: "bg-gray-100 text-gray-700",
};

const HEADERS = [
  "Code",
  "Name",
  "Service",
  "Language Pair",
  "Deadline At",
  "Status",
  "Start At",
  "Completed At",
  "Work Amount",
  "Project",
  "Primary Project Manager",
];

const formatDateTime = (dateStr) => {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  return d.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

// Same columns the table renders, in the same order.
export const VENDOR_JOBS_EXPORT_COLUMNS = [
  { header: "Code", value: (r) => `LT${r.id}` },
  { header: "Name", value: (r) => r.name || "" },
  { header: "Service", value: (r) => r.service?.name || "" },
  {
    header: "Language Pair",
    value: (r) =>
      r.languagePair
        ? `${r.languagePair.sourceLanguage?.name || ""} → ${r.languagePair.targetLanguage?.name || ""}`
        : "",
  },
  { header: "Deadline At", value: (r) => formatDateTime(r.deadline_at) },
  { header: "Status", value: (r) => r.status || "" },
  { header: "Start At", value: (r) => formatDateTime(r.started_at) },
  { header: "Completed At", value: (r) => formatDateTime(r.completed_at) },
  { header: "Work Amount", value: (r) => r.work_amount || "" },
  { header: "Project", value: (r) => r.project?.project_name || "" },
  {
    header: "Primary Project Manager",
    value: (r) =>
      r.project?.primaryManager
        ? `${r.project.primaryManager.first_name} ${r.project.primaryManager.last_name}`
        : "",
  },
];

const VendorJobsTable = ({ data = [] }) => {
  return (
    <div className="w-full bg-white shadow rounded-lg overflow-hidden">
      <div className="max-h-[600px] overflow-y-auto overflow-x-auto">
        <table className="w-full table-auto border-collapse min-w-[1300px]">
          <thead className="sticky top-0 z-10">
            <tr className="bg-white border-b text-[10px] tracking-widest text-gray-500 uppercase">
              {HEADERS.map((h) => (
                <th
                  key={h}
                  className="px-3 py-2 text-center font-semibold whitespace-nowrap"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {data.length === 0 ? (
              <tr>
                <td
                  colSpan={HEADERS.length}
                  className="py-10 text-center text-gray-400 text-xs"
                >
                  No jobs found.
                </td>
              </tr>
            ) : (
              data.map((row, index) => {
                const rowBg = index % 2 === 0 ? "bg-gray-50" : "bg-white";
                const statusCls =
                  STATUS_BADGE[row.status] || "bg-gray-100 text-gray-700";

                return (
                  <tr
                    key={row.id}
                    className={`${rowBg} hover:bg-brand-50 transition-colors`}
                  >
                    <td className="px-3 py-2 text-xs whitespace-nowrap text-center">
                      <Link
                        to={`/vendor/job/${row.id}`}
                        className="text-brand-600 hover:text-brand-800 font-medium underline"
                      >
                        LT{row.id}
                      </Link>
                    </td>

                    <td className="px-3 py-2 text-xs break-words text-gray-900">
                      {row.name || "—"}
                    </td>

                    <td className="px-3 py-2 text-xs break-words text-gray-700">
                      {row.service?.name || "—"}
                    </td>

                    <td className="px-3 py-2 text-xs break-words text-gray-700">
                      {row.languagePair
                        ? `${row.languagePair.sourceLanguage?.name || ""} → ${
                            row.languagePair.targetLanguage?.name || ""
                          }`
                        : "—"}
                    </td>

                    <td className="px-3 py-2 text-xs whitespace-nowrap text-gray-500">
                      {formatDateTime(row.deadline_at)}
                    </td>

                    <td className="px-3 py-2 text-center whitespace-nowrap">
                      <span
                        className={`inline-block text-[10px] font-semibold px-1.5 py-0.5 rounded ${statusCls}`}
                      >
                        {row.status}
                      </span>
                    </td>

                    <td className="px-3 py-2 text-xs whitespace-nowrap text-gray-500">
                      {formatDateTime(row.started_at)}
                    </td>

                    <td className="px-3 py-2 text-xs whitespace-nowrap text-gray-500">
                      {formatDateTime(row.completed_at)}
                    </td>

                    <td className="px-3 py-2 text-xs text-right whitespace-nowrap text-gray-700">
                      {row.work_amount || "—"}
                    </td>

                    <td className="px-3 py-2 text-xs break-words text-gray-700">
                      {row.project?.project_name || "—"}
                    </td>

                    <td className="px-3 py-2 text-xs break-words text-gray-700">
                      {row.project?.primaryManager
                        ? `${row.project.primaryManager.first_name} ${row.project.primaryManager.last_name}`
                        : "—"}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default VendorJobsTable;
